// Choose what the room list shows: home, the people you talk to, one workspace, or your inbox.
// On desktop web it is a rail of squares down the left edge; on phones a row of names across the top.
//
// People appears once you have a direct chat with a person, the workspaces from the second one on. A customer
// with one workspace, no direct chats and no invitation sees no switcher at all, and the list is what it always
// was. Once there is a switcher the inbox is always on it, empty or not: hidden while empty, nobody could find it.
//
// Home is what sits outside the workspaces on the rail: rooms that belong to none of them, direct chats with
// people, and, so that nobody has to look through every workspace for them, everything waiting for an answer.
// A workspace's rooms show inside that workspace. With a single workspace there is no square for it, and home
// is still everything.
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from './Text';
import { useAuth } from '../auth';
import { useRooms, useOpenGates, useInvites, useIsAgent, partitionRooms, threadUnreadTotal, isHidden, Room, isServiceUser } from '../matrix';
import { setPrefs, usePrefs } from '../prefs';
import { makeStyles, radius, useTheme } from '../theme';

export const ALL = 'all'; // home; the id stays 'all' because it is saved in preferences
export const PEOPLE = 'people';
export const INBOX = 'inbox';
export const RAIL_WIDTH = 60;
const SQUARE = 40;

export type Place = {
  id: string; name: string;
  /** Rooms of this workspace, archived ones included: what "belongs here" means for approvals and the archive. */
  rooms: Set<string>;
  unread: number;
  /** Approvals waiting for you in it. */
  waiting: number;
};

/** A direct chat with a person. A direct chat with an agent belongs with the agents. */
export function isPersonChat(room: Room, me: string | null | undefined, isAgent: (uid: string) => boolean): boolean {
  if (!room.isDm || room.isSpace) return false;
  const others = [...new Set([...Object.keys(room.members), ...room.heroes])].filter((u) => u !== me && !isServiceUser(u));
  return others.length > 0 && others.every((u) => !isAgent(u));
}

/** What can be chosen, what is chosen, and how much waits outside it. */
export function useScope() {
  const rooms = useRooms();
  const gates = useOpenGates();
  const invites = useInvites();
  const isAgent = useIsAgent();
  const { userId } = useAuth();
  const { scope: saved, archiveDays } = usePrefs();
  return useMemo(() => {
    const byId = new Map(rooms.map((r) => [r.id, r]));
    const tally = (ids: Set<string>) => {
      let unread = 0;
      ids.forEach((id) => { const r = byId.get(id); if (r) unread += (r.unread || 0) + threadUnreadTotal(r); });
      return { unread, waiting: gates.filter((g) => ids.has(g.room.id)).length };
    };
    const spaces: Place[] = partitionRooms(rooms, archiveDays).spaces.map(({ space }) => {
      const ids = new Set(space.children.filter((id) => { const r = byId.get(id); return !!r && !r.isSpace; }));
      return { id: space.id, name: space.name, rooms: ids, ...tally(ids) };
    });
    const places = spaces.length >= 2 ? spaces : [];            // a single workspace is what "All" already shows
    const chats = rooms.filter((r) => isPersonChat(r, userId, isAgent) && !isHidden(r)).sort((a, b) => (b.last?.ts || 0) - (a.last?.ts || 0));
    const chatIds = new Set(chats.map((r) => r.id));
    const people = { rooms: chats, ids: chatIds, ...tally(chatIds) };
    const inbox = invites.length;
    const show = places.length > 0 || chats.length > 0 || inbox > 0;
    // Something you chose that is no longer there (a workspace you left, your last direct chat) is All. So is an
    // empty inbox left over from the last time the app was open: nobody wants to start on "nothing waiting".
    const valid = saved === PEOPLE ? chats.length > 0 : saved === INBOX ? show && (inbox > 0 || inboxOpened) : places.some((p) => p.id === saved);
    const scope = valid ? saved : ALL;
    // Home's badge counts what sits outside the workspaces on the rail: their squares count their own, and the
    // inbox square the invitations. Without workspace squares, home is everything.
    const railed = new Set<string>();
    for (const p of places) p.rooms.forEach((id) => railed.add(id));
    const homeIds = new Set(rooms.filter((r) => !r.isSpace && !railed.has(r.id)).map((r) => r.id));
    const home = { ids: homeIds, ...tally(homeIds) };
    const here = scope === PEOPLE ? people.waiting : scope === INBOX ? 0 : (places.find((p) => p.id === scope)?.waiting || 0);
    const elsewhere = scope === ALL ? 0 : gates.length - here + (scope === INBOX ? 0 : inbox);
    return { scope, places, people, inbox, show, home, elsewhere };
  }, [rooms, gates, invites, isAgent, userId, saved, archiveDays]);
}
let inboxOpened = false; // the inbox was opened in this run of the app
export const pickScope = (id: string) => { if (id === INBOX) inboxOpened = true; setPrefs({ scope: id }); };

/** "Pristine Pools" → PP, "Ops" → OP, "CB Station" → CB. */
export function initials(name: string): string {
  const words = name.split(/[\s\-_.,:;&/()+'"!?#@]+/).filter(Boolean);
  if (!words.length) return '#';
  return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0]).toUpperCase();
}
const says = (name: string, waiting: number, unread: number) =>
  `${name}${waiting ? `, ${waiting} waiting for you` : ''}${unread ? `, ${unread} unread` : ''}`;

// Three small pictures drawn from plain boxes: the app ships no icon font.
function PersonGlyph({ color }: { color: string }) {
  return (
    <View style={{ width: 18, height: 18, alignItems: 'center', justifyContent: 'flex-end' }}>
      <View style={{ width: 8, height: 8, borderRadius: 4, borderWidth: 1.7, borderColor: color, marginBottom: 1.5 }} />
      <View style={{ width: 15, height: 7, borderTopLeftRadius: 7.5, borderTopRightRadius: 7.5, borderWidth: 1.7, borderBottomWidth: 0, borderColor: color }} />
    </View>
  );
}
function InboxGlyph({ color }: { color: string }) {
  return (
    <View style={{ width: 19, height: 15, borderRadius: 3.5, borderWidth: 1.7, borderColor: color, justifyContent: 'flex-end' }}>
      <View style={{ height: 5.5, borderTopWidth: 1.7, borderColor: color }} />
    </View>
  );
}
function HomeGlyph({ color }: { color: string }) {
  // A roof (the top half of a tilted square) over walls with no top edge.
  return (
    <View style={{ width: 18, height: 18, alignItems: 'center', justifyContent: 'flex-end' }}>
      <View style={{ width: 18, height: 8, overflow: 'hidden', alignItems: 'center' }}>
        <View style={{ width: 12, height: 12, marginTop: 2.5, borderWidth: 1.7, borderColor: color, transform: [{ rotate: '45deg' }] }} />
      </View>
      <View style={{ width: 13, height: 8, borderWidth: 1.7, borderTopWidth: 0, borderColor: color }} />
    </View>
  );
}

type Item = { id: string; name: string; short?: string; glyph?: 'home' | 'person' | 'inbox'; unread: number; waiting: number };

function Square({ it, on }: { it: Item; on: boolean }) {
  const theme = useTheme(); const s = useStyles();
  const [over, setOver] = useState(false);
  const color = on ? theme.accent : theme.muted;
  return (
    <View style={[s.item, over && { zIndex: 5 }]}>
      <Pressable
        onPress={() => pickScope(it.id)} onHoverIn={() => setOver(true)} onHoverOut={() => setOver(false)}
        accessibilityRole="tab" accessibilityLabel={says(it.name, it.waiting, it.unread)} accessibilityState={{ selected: on }} aria-selected={on}
        style={({ pressed }) => [s.square, on && s.squareOn, pressed && { opacity: 0.7 }]}
      >
        {it.glyph === 'home' ? <HomeGlyph color={color} /> : it.glyph === 'person' ? <PersonGlyph color={color} /> : it.glyph === 'inbox' ? <InboxGlyph color={color} />
          : <Text style={[s.squareText, { color }, (it.short || '').length > 2 && { fontSize: 12 }]}>{it.short}</Text>}
        {it.waiting > 0 ? <View style={s.count}><Text style={s.countText}>{it.waiting > 9 ? '9+' : it.waiting}</Text></View>
          : it.unread > 0 ? <View style={s.dot} /> : null}
      </Pressable>
      {over && <View style={s.tip} pointerEvents="none"><Text style={s.tipText} numberOfLines={1}>{it.name}</Text></View>}
    </View>
  );
}

/** Desktop web: squares down the left edge, the name on hover. The inbox sits at the bottom. */
export function WorkspaceRail() {
  const s = useStyles();
  const { scope, places, people, inbox, show, home } = useScope();
  if (!show) return null;
  return (
    <View style={s.rail} accessibilityRole="tablist" accessibilityLabel="What the list shows">
      <Square it={{ id: ALL, name: 'Home', glyph: 'home', unread: home.unread, waiting: home.waiting }} on={scope === ALL} />
      {people.rooms.length > 0 && <Square it={{ id: PEOPLE, name: 'People', glyph: 'person', unread: people.unread, waiting: people.waiting }} on={scope === PEOPLE} />}
      {places.length > 0 && <View style={s.rule} />}
      {places.map((p) => <Square key={p.id} it={{ ...p, short: initials(p.name) }} on={scope === p.id} />)}
      <View style={{ flex: 1 }} />
      <Square it={{ id: INBOX, name: 'Inbox', glyph: 'inbox', unread: 0, waiting: inbox }} on={scope === INBOX} />
    </View>
  );
}

/** Phones, and a narrow browser window: the names in a row that scrolls sideways. */
export function WorkspaceChips() {
  const s = useStyles();
  const { scope, places, people, inbox, show, home } = useScope();
  if (!show) return null;
  const items: Item[] = [
    { id: ALL, name: 'Home', unread: home.unread, waiting: home.waiting },
    { id: INBOX, name: 'Inbox', unread: 0, waiting: inbox },
    ...(people.rooms.length > 0 ? [{ id: PEOPLE, name: 'People', unread: people.unread, waiting: people.waiting }] : []),
    ...places,
  ];
  return (
    <View style={s.chipsWrap}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chips} keyboardShouldPersistTaps="handled">
        {items.map((it) => {
          const on = it.id === scope;
          return (
            <Pressable key={it.id} onPress={() => pickScope(it.id)} hitSlop={4}
              accessibilityRole="tab" accessibilityLabel={says(it.name, it.waiting, it.unread)} accessibilityState={{ selected: on }} aria-selected={on}
              style={({ pressed }) => [s.chip, on && s.chipOn, pressed && { opacity: 0.7 }]}>
              <Text style={[s.chipText, on && s.chipTextOn]} numberOfLines={1}>{it.name}</Text>
              {it.waiting > 0 ? <View style={s.chipCount}><Text style={s.countText}>{it.waiting > 9 ? '9+' : it.waiting}</Text></View>
                : it.unread > 0 ? <View style={s.chipDot} /> : null}
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  rail: { width: RAIL_WIDTH, paddingVertical: 12, alignItems: 'center', gap: 8, backgroundColor: theme.sidebar, borderRightWidth: 1, borderRightColor: theme.border, zIndex: 2 },
  item: { width: SQUARE, height: SQUARE },
  square: { width: SQUARE, height: SQUARE, borderRadius: radius.md, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.surface2, borderWidth: 1, borderColor: theme.border },
  squareOn: { backgroundColor: theme.accentBg, borderColor: theme.accentBorder },
  squareText: { fontSize: 13.5, fontWeight: '700' },
  rule: { width: 24, height: 1, backgroundColor: theme.border },
  count: { position: 'absolute', top: -5, right: -6, minWidth: 18, height: 18, borderRadius: 9, paddingHorizontal: 4, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.agent, borderWidth: 2, borderColor: theme.sidebar },
  countText: { color: theme.agentInk, fontSize: 11, fontWeight: '800' },
  dot: { position: 'absolute', top: -3, right: -3, width: 10, height: 10, borderRadius: 5, backgroundColor: theme.accent, borderWidth: 2, borderColor: theme.sidebar },
  tip: { position: 'absolute', left: SQUARE + 12, top: 7, backgroundColor: theme.ink, borderRadius: radius.sm, paddingHorizontal: 9, paddingVertical: 5 },
  tipText: { color: theme.bg, fontSize: 12.5, fontWeight: '600' },
  chipsWrap: { borderBottomWidth: 1, borderBottomColor: theme.border },
  chips: { paddingHorizontal: 12, paddingVertical: 8, gap: 8, alignItems: 'center' },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 6, maxWidth: 200, paddingHorizontal: 12, paddingVertical: 7, borderRadius: radius.pill, backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border },
  chipOn: { backgroundColor: theme.accentBg, borderColor: theme.accentBorder },
  chipText: { color: theme.muted, fontSize: 13.5, fontWeight: '600', flexShrink: 1 },
  chipTextOn: { color: theme.accent },
  chipCount: { minWidth: 18, height: 18, borderRadius: 9, paddingHorizontal: 4, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.agent },
  chipDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: theme.accent },
}));
